import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const programs = [
  {
    icon: 'ri-home-heart-line',
    label: 'In-Home',
    title: 'In-Home Programs',
    desc: 'We come to you. One-on-one sessions in your home with all equipment provided.',
    path: '/in-home-programs',
    gradient: 'from-[#5B21B6] to-[#7C3AED]',
    light: 'bg-[#F5F3FF] text-[#5B21B6]',
  },
  {
    icon: 'ri-mental-health-line',
    label: 'Neurodivergent',
    title: 'Neurodivergent Development',
    desc: 'Purpose-built programs for individuals with autism, ADHD, and diverse learning styles.',
    path: '/neurodivergent-development',
    gradient: 'from-[#B45309] to-[#D97706]',
    light: 'bg-[#FFFBEB] text-[#B45309]',
  },
  {
    icon: 'ri-mic-line',
    label: 'Studio',
    title: 'Recording Studio',
    desc: 'Professional recording, mixing, and mastering for artists and content creators.',
    path: '/recording-studio',
    gradient: 'from-[#0F172A] to-[#1E3A8A]',
    light: 'bg-slate-100 text-slate-700',
  },
  {
    icon: 'ri-calendar-event-line',
    label: 'Off-Site',
    title: 'Off-Site Services',
    desc: 'Live entertainment, production, media, and creative direction for events and brands.',
    path: '/off-site-services',
    gradient: 'from-[#065F46] to-[#059669]',
    light: 'bg-emerald-50 text-emerald-700',
  },
  {
    icon: 'ri-links-line',
    label: 'Free Service',
    title: 'Ministry Connections',
    desc: 'Connecting creatives and ministry professionals with churches and organizations — at no cost.',
    path: '/ministry-connections',
    gradient: 'from-[#78350F] to-[#B45309]',
    light: 'bg-amber-50 text-amber-700',
  },
];

export default function ProgramsShowcase() {
  const { ref: titleRef, isVisible: titleVis } = useScrollAnimation({ threshold: 0.15 });
  const { ref: gridRef, isVisible: gridVis } = useScrollAnimation({ threshold: 0.05 });

  return (
    <section className="py-28 bg-white relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 15% 50%, rgba(238,242,255,0.8) 0%, transparent 50%), radial-gradient(circle at 85% 20%, rgba(245,243,255,0.6) 0%, transparent 40%)' }} />

      <div className="relative z-10 max-w-screen-xl mx-auto px-8">

        {/* Header */}
        <div ref={titleRef} className="text-center mb-16">
          <span className={`scroll-hidden ${titleVis ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-purple/10 text-brand-purple font-body text-xs font-semibold uppercase tracking-widest mb-5`}>
            Everything We Offer
          </span>
          <h2 className={`scroll-hidden delay-100 ${titleVis ? 'scroll-visible' : ''} font-heading text-5xl lg:text-6xl font-black text-brand-blue-dark mb-4 leading-tight`}>
            One Place. <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1E3A8A] to-[#7C3AED]">Every Creative Need.</span>
          </h2>
          <p className={`scroll-hidden delay-200 ${titleVis ? 'scroll-visible' : ''} font-body text-gray-500 text-base max-w-2xl mx-auto leading-relaxed`}>
            From your first lesson to full professional production — Creative Institute has a program or service built for exactly where you are and where you want to go.
          </p>
        </div>

        {/* Programs grid — 2 top, 3 bottom */}
        <div ref={gridRef} className="flex flex-col gap-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:max-w-2xl md:mx-auto w-full">
            {programs.slice(0, 2).map((p, i) => (
              <ProgramCard key={p.title} program={p} isVisible={gridVis} delay={i * 100} />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {programs.slice(2).map((p, i) => (
              <ProgramCard key={p.title} program={p} isVisible={gridVis} delay={(i + 2) * 100} />
            ))}
          </div>
        </div>

        {/* Bottom finder CTA */}
        <div className={`scroll-hidden delay-500 ${gridVis ? 'scroll-visible' : ''} mt-14 flex flex-col sm:flex-row items-center justify-center gap-4`}>
          <Link
            to="/program-finder"
            className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] text-white font-body font-bold text-base cursor-pointer hover:opacity-90 transition-opacity"
          >
            <i className="ri-search-line text-lg" /> Find My Perfect Program
          </Link>
          <Link
            to="/services"
            className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-gray-200 text-gray-600 font-body font-semibold text-base cursor-pointer hover:border-brand-purple hover:text-brand-purple transition-colors"
          >
            View All Services <i className="ri-arrow-right-line" />
          </Link>
        </div>

      </div>
    </section>
  );
}

function ProgramCard({ program, isVisible, delay }: {
  program: typeof programs[0]; isVisible: boolean; delay: number;
}) {
  return (
    <Link
      to={program.path}
      className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} group relative rounded-3xl overflow-hidden bg-white border-2 border-gray-100 card-lift hover:border-transparent flex flex-col cursor-pointer block`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Gradient header */}
      <div className={`bg-gradient-to-br ${program.gradient} p-8 relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ backgroundImage: 'radial-gradient(circle at 70% 30%, rgba(255,255,255,0.1) 0%, transparent 60%)' }} />
        <span className={`inline-block px-3 py-1 rounded-full font-body text-[10px] font-semibold uppercase tracking-widest mb-4 ${program.light}`}>
          {program.label}
        </span>
        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/20 text-white mb-1 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
          <i className={`${program.icon} text-2xl`} />
        </div>
      </div>

      {/* Content */}
      <div className="p-7 flex-1 flex flex-col">
        <h3 className="font-heading text-brand-blue-dark text-xl font-black mb-2 group-hover:text-brand-purple transition-colors duration-300">
          {program.title}
        </h3>
        <p className="font-body text-gray-500 text-sm leading-relaxed flex-1">{program.desc}</p>
        <div className="mt-5 flex items-center gap-2 font-body text-brand-purple text-sm font-semibold group-hover:gap-3 transition-all duration-300">
          Learn More <i className="ri-arrow-right-line" />
        </div>
      </div>

      {/* Bottom gradient bar on hover */}
      <div className={`h-1 bg-gradient-to-r ${program.gradient} w-0 group-hover:w-full transition-all duration-500`} />
    </Link>
  );
}
